import {
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Paper,
  LinearProgress,
  Box,
} from "@mui/material";
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FormAlert from "../../../components/FormAlert";
import { makeStyles } from "@mui/styles";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import { productBulkUpdate } from "../../../services/Export";

const useStyles = makeStyles({
  fileContainer: {
    padding: "20px",
    marginTop: "15px",
    marginBottom: "15px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    border: "2px dashed #ccc",
    borderRadius: "5px",
    cursor: "pointer",
  },
  selectedFile: {
    padding: "10px",
    marginTop: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "50%",
  },
  fileInput: {
    display: "none",
  },
  progressContainer: {
    width: "100%",
    marginTop: "15px",
  },
});

function Ex_BulkUpdate({ setOpenBulkUpdateForm }) {
  const classes = useStyles();

  // States
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Alert states
  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    message: "",
  });

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];

    if (file) {
      // Check if file is Excel
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (fileExtension === "xlsx" || fileExtension === "xls") {
        setSelectedFile(file);
        closeAlert();
      } else {
        setAlertData({
          type: "error",
          message: "Please select a valid Excel file (.xlsx or .xls)",
        });
        setAlert(true);
        setSelectedFile(null);
      }
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    console.log("Upload file 1");
    if (!selectedFile) return;
    console.log("Upload file 2");

    setIsUploading(true);
    setUploadProgress(0);

    // In a real application, you would replace this with your actual upload logic:

    try {
      // Simulate upload progress
      const simulateProgress = () => {
        setUploadProgress((prevProgress) => {
          if (prevProgress >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prevProgress + 10;
        });
      };

      console.log("Upload file3");
      const formData = new FormData();
      formData.append("file", selectedFile);
      console.log("form Data", formData);
      const response = await productBulkUpdate(formData);
      const progressInterval = setInterval(simulateProgress, 500);
      setIsUploading(false);

      if (response == "Success") {
        setAlertData({
          type: "success",
          message: "File uploaded successfully!",
        });
      } else {
        setAlertData({
          type: "error",
          message: "Upload failed. Please try again.",
        });
      }
    } catch (error) {
      setAlertData({
        type: "error",
        message: "An error occurred during upload.",
      });
    } finally {
      setIsUploading(false);
      setAlert(true);
    }
  };

  // Reset form
  const resetForm = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    closeAlert();
  };

  // Close alert
  const closeAlert = () => {
    setAlert(false);
    setAlertData({
      type: "",
      message: "",
    });
  };

  // Remove selected file
  const removeFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={11} sx={12}>
        <Typography mt={1} variant="h6">
          Product Bulk Update
        </Typography>
      </Grid>
      <Grid item xs={12} sm={1} sx={12}>
        <Tooltip title="Close">
          <IconButton
            onClick={() => {
              setOpenBulkUpdateForm(false);
            }}
            aria-label="close"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Grid>

      <Grid item xs={12} sm={12} sx={12}>
        <input
          accept=".xlsx, .xls"
          className={classes.fileInput}
          id="excel-file-input"
          type="file"
          onChange={handleFileSelect}
          disabled={isUploading}
        />
        <label htmlFor="excel-file-input">
          <Paper className={classes.fileContainer} component="div">
            <UploadFileIcon fontSize="large" color="primary" />
            <Typography variant="body1" mt={1}>
              {isUploading
                ? "Uploading..."
                : "Click to browse or drag and drop"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Only Excel files (.xlsx, .xls) are accepted
            </Typography>
          </Paper>
        </label>
      </Grid>

      {selectedFile && (
        <Grid item xs={12} sm={12} sx={12}>
          <Paper
            className={classes.selectedFile}
            style={{
              backgroundColor: "#e8fae0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <TextSnippetIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body2" noWrap>
                {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </Typography>
            </Box>
            {!isUploading && (
              <IconButton
                size="small"
                onClick={removeFile}
                disabled={isUploading}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>

          {isUploading && (
            <Box className={classes.progressContainer}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" align="right" color="textSecondary">
                {`${Math.round(uploadProgress)}%`}
              </Typography>
            </Box>
          )}
        </Grid>
      )}

      <Grid item xs={12} sm={12} sx={12}>
        {displayAlert ? (
          <FormAlert type={alertData.type} message={alertData.message} />
        ) : null}
      </Grid>

      <Grid item xs={12} sm={12} sx={12}>
        <Grid container justifyContent="space-between">
          <Grid item xs={12} sm={4} sx={12}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? "Uploading..." : "Upload File"}
            </Button>
          </Grid>
          <Grid item xs={12} sm={1} sx={12}>
            <Button
              onClick={resetForm}
              variant="outlined"
              disabled={isUploading}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default Ex_BulkUpdate;
